import React, { useState, useRef } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  Card,
  CardContent,
  Slide,
  Tabs,
  Tab,
  ButtonBase,
  Box,
  Chip,
  Divider,
  Fade,
  Grow
} from '@mui/material';
import axios from 'axios';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  SolarPower as SolarPowerIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  ElectricBolt as ElectricBoltIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import PrequalAssistant from './PrequalAssistant';
import { styled } from '@mui/material/styles';
import { AppCTX } from '../data/AppContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import NotesDrawer from './NotesDrawer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import SolarInsights from './SolarInsights';

// Enhanced styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  borderRadius: 16,
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiFilledInput-root': {
    background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #222 0%, #2f2f2f 100%)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused': {
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      border: '2px solid #1976d2',
      boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#bbb',
    fontWeight: 500,
  },
  '& .MuiInputBase-input': {
    color: '#fff',
    fontWeight: 500,
    fontSize: 16,
  }
}));

const StatusButton = styled(ButtonBase)(({ selected, disabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 16,
  padding: 16,
  minWidth: 100,
  minHeight: 100,
  background: selected
    ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
    : 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
  border: selected
    ? '2px solid #1976d2'
    : '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: selected
    ? '0 8px 32px rgba(25, 118, 210, 0.3), 0 4px 16px rgba(25, 118, 210, 0.2)'
    : '0 4px 16px rgba(0, 0, 0, 0.2)',
  transform: selected ? 'scale(1.05)' : 'scale(1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  '&:hover': {
    transform: disabled ? 'scale(1)' : (selected ? 'scale(1.08)' : 'scale(1.02)'),
    boxShadow: disabled ? '0 4px 16px rgba(0, 0, 0, 0.2)' : '0 12px 40px rgba(0, 0, 0, 0.3)',
  }
}));

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Status icon mapping using PNGs from public folder
const statusIcons = {
  F: '/badcredit.png',
  NI: '/notinterested.png',
  NH: '/nothome.png',
  CB: '/comebacklater.png',
  sold_alarm: '/alaramsinstalled.png',
  sold_solar: '/solarinstalled.png',
  default: '/default.png'
};

const markerTypes = [
  { key: 'sold_alarm', label: 'Sold Alarm', icon: statusIcons.sold_alarm, color: '#4caf50' },
  { key: 'sold_solar', label: 'Sold Solar', icon: statusIcons.sold_solar, color: '#ff9800' },
  { key: 'F', label: 'Failed Credit', icon: statusIcons.F, color: '#f44336' },
  { key: 'NI', label: 'Not Interested', icon: statusIcons.NI, color: '#9c27b0' },
  { key: 'NH', label: 'Not Home', icon: statusIcons.NH, color: '#ffc107' },
  { key: 'CB', label: 'Call Back', icon: statusIcons.CB, color: '#009688' },
  { key: 'default', label: 'Prospect', icon: statusIcons.default, color: '#2196f3' },
];

function a11yProps(index) {
  return {
    id: `client-tab-${index}`,
    'aria-controls': `client-tabpanel-${index}`,
  };
}

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={300}>
          <Box sx={{ p: 0 }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

export default function ClientDetails({ open, selectedProspect, setSelectedProspect, setShowDetails }) {
  const { updateProspect, user, setStatus, useApiClient } = React.useContext(AppCTX);
  const apiClient = useApiClient();
  const [owner1name, setowner1name] = useState('');
  const [owner1number, setowner1number] = useState('');
  const [owner2name, setowner2name] = useState('');
  const [owner2number, setowner2number] = useState('');
  const [utilityBill, setutilityBill] = useState(null);
  const [notes, setNotes] = useState([]);
  const [inputText, setInputText] = useState('');
  const [owners, setOwners] = useState([]);
  const [ownerTab, setOwnerTab] = useState(0);
  const [markerStatus, setMarkerStatus] = useState('');
  const [tabValue, setTabValue] = React.useState(0);
  const [notesDrawerOpen, setNotesDrawerOpen] = React.useState(false);
  const [monthlyConsumption, setMonthlyConsumption] = useState([
    820, 760, 900, 950, 1100, 1200, 1300, 1250, 1050, 950, 870, 800
  ]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const yearlyAverage = Math.round(monthlyConsumption.reduce((a, b) => a + b, 0) / 12);
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(new Date().getMonth());
  const [copied, setCopied] = useState(false);
  const [solarData, setSolarData] = useState(null);
  const [solarLoading, setSolarLoading] = useState(false);
  const [openPrequal, setOpenPrequal] = useState(false);

  React.useEffect(() => {
    if (selectedProspect != null) {
      if (selectedProspect.utilityBill != null) {
        setutilityBill(selectedProspect.utilityBill);
      }
      setOwners(selectedProspect.owners && selectedProspect.owners.length > 0 ? [...selectedProspect.owners] : [{ name: '', number: '' }]);
      setNotes(selectedProspect.notes);
      setMarkerStatus(selectedProspect.markerStatus || '');
    } else {
      setowner1name('');
      setowner1number('');
      setutilityBill(null);
      setNotes([]);
      setInputText('');
      setOwners([{ name: '', number: '' }]);
      setMarkerStatus('');
    }
  }, [selectedProspect]);

  const handleOwnerChange = (idx, field, value) => {
    const updatedOwners = [...owners];
    updatedOwners[idx][field] = value;
    setOwners(updatedOwners);
  };

  const handleSave = async () => {
    try {
      let update = { ...selectedProspect };
      update.owners = owners;
      update.utilityBill = utilityBill;
      update.notes = notes;
      await updateProspect(update);
    } catch (err) {
      console.log('Error:', err);
    }
    setShowDetails(false);
    setSelectedProspect(null);
  };

  const handleClose = () => {
    setShowDetails(false);
    setSelectedProspect(null);
  };

  const addNote = () => {
    if (inputText.trim() !== '') {
      const update = [...notes];
      update.push(user.data.name + " - " + inputText);
      setInputText('');
      setNotes(update);
    }
  };

  const uploadUtilityBill = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const presign = await apiClient.post('/api/uploads/utility-bill', {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      if (!presign.success) throw new Error(presign.error || 'Failed to get upload URL');
      await axios.put(presign.uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      const url = presign.fileUrl || presign.key;
      setutilityBill(url);
      setStatus({ notificationType: 'success', message: 'Utility bill uploaded' });
    } catch (err) {
      console.error(err);
      setStatus({ notificationType: 'error', message: 'Upload failed' });
    }
  };

  const handleAddOwner = () => {
    setOwners([...owners, { name: '', number: '' }]);
    setOwnerTab(owners.length);
  };

  const handleMarkerStatusChange = (status) => {
    setMarkerStatus(status);
    if (selectedProspect) {
      const update = {
        ...selectedProspect,
        markerStatus: status,
        systemInstalled: status === 'closed' ? true : false
      };
      setSelectedProspect(update);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  };

  const getSolarInsights = async () => {
    if (!selectedProspect?.coords?.lat || !selectedProspect?.coords?.lng) {
      setStatus({ notificationType: 'error', message: 'No coordinates available for this property' });
      return;
    }

    setSolarLoading(true);
    try {
      const { lat, lng } = selectedProspect.coords;
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        setStatus({ notificationType: 'error', message: 'Google Maps API key not configured' });
        return;
      }

      const url = `https://solar.googleapis.com/v1/buildingInsights:findClosest?location.latitude=${lat}&location.longitude=${lng}&key=${apiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        // Handle 404 Not Found specifically (no solar data available)
        if (response.status === 404) {
          setSolarData({ noData: true });
          setStatus({ notificationType: 'info', message: 'Solar data not available for this location' });
          return;
        }
        throw new Error(data.error?.message || 'Failed to fetch solar data');
      }

      setSolarData(data);
      setStatus({ notificationType: 'success', message: 'Solar insights retrieved successfully' });
    } catch (error) {
      console.error('Solar API error:', error);
      setStatus({ notificationType: 'error', message: error.message || 'Failed to get solar insights' });
    } finally {
      setSolarLoading(false);
    }
  };

  // Find the yearly average consumption from the monthlyConsumption array if available
  const yearlyAverageConsumption = Array.isArray(monthlyConsumption)
    ? Math.round(monthlyConsumption.reduce((a, b) => a + b, 0))
    : 0;

  return (
    <Dialog
      id="clientDetailsDialog"
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        '& .MuiDialog-paper': {
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
        }
      }}
    >
      <AppBar sx={{
        position: 'relative',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            sx={{
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{
            ml: 2,
            flex: 1,
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '0.5px'
          }} variant="h6">
            Home Details
          </Typography>
          <Button
            sx={{
              color: '#fff',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              mr: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
              }
            }}
            onClick={() => setOpenPrequal(true)}
            startIcon={<SecurityIcon />}
          >
            Prequalify
          </Button>
          <Button
            sx={{
              color: '#fff',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              '&:hover': {
                background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
              }
            }}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Toolbar>
      </AppBar>

      {/* Enhanced Status Markers Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        py: 3,
        position: 'relative'
      }}>
        {/* Left Scroll Indicator */}
        <Box sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 40,
          height: 80,
          background: 'linear-gradient(90deg, rgba(26,26,26,0.9) 0%, rgba(26,26,26,0.3) 50%, transparent 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <Box sx={{
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '12px solid rgba(255,255,255,0.6)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
          }} />
        </Box>

        {/* Right Scroll Indicator */}
        <Box sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 40,
          height: 80,
          background: 'linear-gradient(270deg, rgba(26,26,26,0.9) 0%, rgba(26,26,26,0.3) 50%, transparent 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <Box sx={{
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderLeft: '12px solid rgba(255,255,255,0.6)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
          }} />
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}>
          <Swiper
            slidesPerView={3.5}
            spaceBetween={12}
            centeredSlides={false}
            grabCursor={true}
            style={{
              width: '100%',
              padding: '8px 0',
              minHeight: 140
            }}
            breakpoints={{
              320: {
                slidesPerView: 2.5,
                spaceBetween: 8
              },
              480: {
                slidesPerView: 3.5,
                spaceBetween: 12
              },
              768: {
                slidesPerView: 4.5,
                spaceBetween: 16
              },
              1024: {
                slidesPerView: 5.5,
                spaceBetween: 20
              }
            }}
          >
            {markerTypes.map((type, idx) => {
              const isClosed = type.key === 'closed';
              const canSetClosed = user?.data?.memberType === 'superuser' || user?.data?.memberType === 'closer';
              const isDisabled = isClosed && !canSetClosed;
              const isSelected = markerStatus === type.key;

              return (
                <SwiperSlide
                  key={type.key}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '4px'
                  }}
                >
                  <Grow in={true} timeout={300 + idx * 100}>
                    <StatusButton
                      selected={isSelected}
                      disabled={isDisabled}
                      onClick={() => !isDisabled && handleMarkerStatusChange(type.key)}
                    >
                      <Box sx={{
                        position: 'relative',
                        mb: 1
                      }}>
                        <img
                          src={type.icon}
                          alt={type.label}
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            filter: isSelected ? 'brightness(1.2)' : 'brightness(1)'
                          }}
                        />
                        {isSelected && (
                          <CheckCircleIcon
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              color: '#4caf50',
                              fontSize: 20,
                              background: '#fff',
                              borderRadius: '50%'
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isSelected ? '#fff' : '#bbb',
                          fontWeight: isSelected ? 700 : 500,
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          lineHeight: 1.2
                        }}
                      >
                        {type.label}
                      </Typography>
                    </StatusButton>
                  </Grow>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Box>
      </Box>

      <Box sx={{
        height: "calc(100vh - 200px)",
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
        overflow: 'hidden'
      }}>
        {/* Enhanced Tab Navigation */}
        <Box sx={{
          width: '100%',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.02)',
          px: 3,
          pt: 2
        }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="client details tabs"
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                color: '#bbb',
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                minHeight: 48,
                '&:hover': {
                  color: '#fff',
                  background: 'rgba(255, 255, 255, 0.05)',
                }
              },
              '& .Mui-selected': {
                color: '#1976d2',
                fontWeight: 700,
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '2px 2px 0 0',
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
              }
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Owners
                </Box>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon fontSize="small" />
                  Property
                </Box>
              }
              {...a11yProps(1)}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ElectricBoltIcon fontSize="small" />
                  Utility
                </Box>
              }
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ height: 'calc(100% - 64px)', overflow: 'hidden' }}>
          <CustomTabPanel value={tabValue} index={0}>
            <Box sx={{
              height: 'calc(100vh - 300px)',
              overflowY: 'auto',
              p: 3,
              background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
              pb: 20
            }}>
              <Grow in={true} timeout={400}>
                <StyledCard sx={{ mb: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <PersonIcon sx={{ color: '#1976d2', mr: 1, fontSize: 28 }} />
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        Owner Information
                      </Typography>
                    </Box>

                    <Tabs
                      value={ownerTab}
                      onChange={(_, newValue) => setOwnerTab(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        mb: 3,
                        minHeight: 48,
                        '& .MuiTab-root': {
                          color: '#bbb',
                          minWidth: 100,
                          fontWeight: 600,
                          '&:hover': { color: '#fff' }
                        },
                        '& .Mui-selected': {
                          color: '#1976d2',
                          fontWeight: 700
                        },
                        '& .MuiTabs-indicator': {
                          background: '#1976d2'
                        }
                      }}
                    >
                      {owners.map((_, idx) => (
                        <Tab key={idx} label={`Owner ${idx + 1}`} />
                      ))}
                      <Tab
                        icon={<span style={{ fontSize: 20, fontWeight: 700 }}>+</span>}
                        aria-label="add owner"
                        onClick={handleAddOwner}
                      />
                    </Tabs>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <StyledTextField
                        fullWidth
                        label="Full Name"
                        variant="filled"
                        value={owners[ownerTab]?.name || ''}
                        onChange={(e) => handleOwnerChange(ownerTab, 'name', e.target.value)}
                        InputLabelProps={{ style: { color: '#bbb' } }}
                      />
                      <StyledTextField
                        fullWidth
                        label="Phone Number"
                        variant="filled"
                        value={owners[ownerTab]?.number || ''}
                        onChange={(e) => handleOwnerChange(ownerTab, 'number', e.target.value)}
                        InputLabelProps={{ style: { color: '#bbb' } }}
                      />
                    </Stack>
                  </CardContent>
                </StyledCard>
              </Grow>
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={tabValue} index={1}>
            <Box sx={{
              height: 'calc(100vh - 300px)',
              overflowY: 'auto',
              p: 3,
              background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
              pb: 20
            }}>
              <Grow in={true} timeout={400}>
                <StyledCard>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <HomeIcon sx={{ color: '#1976d2', mr: 1, fontSize: 28 }} />
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        Property Details
                      </Typography>
                    </Box>

                    <Typography variant="subtitle1" sx={{ color: '#bbb', fontWeight: 600, mb: 2 }}>
                      Address
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        mb: 3,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #222 0%, #2f2f2f 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                      onClick={() => {
                        if (selectedProspect?.address) {
                          navigator.clipboard.writeText(selectedProspect.address);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1200);
                        }
                      }}
                    >
                      <Tooltip title={copied ? 'Copied!' : 'Copy address'} arrow open={copied ? true : undefined}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography variant="body1" sx={{ color: '#fff', flex: 1, fontWeight: 500 }}>
                            {selectedProspect?.address}
                          </Typography>
                          <ContentCopyIcon sx={{
                            color: copied ? '#4caf50' : '#bbb',
                            ml: 1,
                            fontSize: 20,
                            transition: 'all 0.3s ease'
                          }} />
                        </Box>
                      </Tooltip>
                    </Box>

                    <Typography variant="subtitle1" sx={{ color: '#bbb', fontWeight: 600, mb: 2 }}>
                      Property Specifications
                    </Typography>
                    <TableContainer component={Paper} sx={{
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      overflow: 'hidden'
                    }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ color: '#bbb', fontWeight: 600, border: 'none', py: 2 }}>Lot Sqft:</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, border: 'none', py: 2 }}>7,200</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#bbb', fontWeight: 600, border: 'none', py: 2 }}>Roof Measurements:</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, border: 'none', py: 2 }}>2,100 sqft</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#bbb', fontWeight: 600, border: 'none', py: 2 }}>Build Date:</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, border: 'none', py: 2 }}>1998</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#bbb', fontWeight: 600, border: 'none', py: 2 }}>Rooms:</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, border: 'none', py: 2 }}>4 bed, 3 bath</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              </Grow>
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={tabValue} index={2}>
            <Box sx={{
              height: 'calc(100vh - 320px)',
              overflowY: 'auto',
              p: 3,
              background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
              pb: 20
            }}>
              {/* Solar Insights Card */}
              <SolarInsights selectedProspect={selectedProspect} setStatus={setStatus} yearlyConsumption={yearlyAverageConsumption} />

              {/* Enhanced Energy Consumption Chart */}
              <Grow in={true} timeout={400}>
                <StyledCard sx={{ mb: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{
                    p: 3,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    alignItems: 'flex-start'
                  }}>
                    {/* Flexible Header */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      flexWrap: 'wrap',
                      gap: 2,
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 200 }}>
                        <ElectricBoltIcon sx={{ color: '#ff9800', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                          Monthly Energy Consumption
                        </Typography>
                      </Box>
                      <Chip
                        label={`${yearlyAverage} kWh/mo avg`}
                        sx={{
                          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          flexShrink: 0
                        }}
                      />
                    </Box>

                    {/* Flexible Chart Container */}
                    <Box sx={{
                      width: '100%',
                      flex: 1,
                      minHeight: 250,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible'
                    }}>
                      <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                        <BarChart
                          data={months.map((month, i) => ({ month, kWh: monthlyConsumption[i] }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis
                            dataKey="month"
                            stroke="#bbb"
                            fontSize={12}
                            tick={{ fill: '#bbb' }}
                            dy={10}
                          />
                          <YAxis
                            stroke="#bbb"
                            fontSize={12}
                            tick={{ fill: '#bbb' }}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: 8,
                              color: '#fff',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                            }}
                          />
                          <Bar
                            dataKey="kWh"
                            fill="url(#energyGradient)"
                            radius={[4, 4, 0, 0]}
                          />
                          <defs>
                            <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#1976d2" />
                              <stop offset="100%" stopColor="#1565c0" />
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Flexible Bottom Section */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      flexShrink: 0,
                      mt: 2,
                      width: '100%'
                    }}>
                      {/* Month Selection Tabs */}
                      <Box sx={{ width: '100%' }}>
                        <Tabs
                          value={selectedMonthIdx}
                          onChange={(e, newValue) => setSelectedMonthIdx(newValue)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            '& .MuiTab-root': {
                              color: '#bbb',
                              fontWeight: 600,
                              fontSize: 12,
                              minWidth: 50,
                              maxWidth: 80,
                              '&:hover': { color: '#fff' }
                            },
                            '& .Mui-selected': {
                              color: '#1976d2',
                              fontWeight: 700,
                            },
                            '& .MuiTabs-indicator': {
                              background: '#1976d2'
                            }
                          }}
                        >
                          {months.map((month, i) => (
                            <Tab key={month} label={month} />
                          ))}
                        </Tabs>
                      </Box>

                      {/* Month Usage Input */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                        background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        flexWrap: 'wrap',
                        gap: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#bbb', fontWeight: 600 }}>
                          {months[selectedMonthIdx]} usage:
                        </Typography>
                        <StyledTextField
                          type="number"
                          value={monthlyConsumption[selectedMonthIdx]}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10) || 0;
                            setMonthlyConsumption(cons => cons.map((v, idx) => idx === selectedMonthIdx ? val : v));
                          }}
                          inputProps={{
                            min: 0,
                            style: {
                              color: '#fff',
                              textAlign: 'center',
                              fontSize: 20,
                              fontWeight: 700,
                              padding: '8px 12px'
                            }
                          }}
                          variant="filled"
                          sx={{
                            width: 120,
                            '& .MuiFilledInput-root': {
                              minHeight: 48
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ color: '#bbb', fontWeight: 500 }}>
                          kWh
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grow>

              {/* Enhanced Utility Bill Section */}
              <Grow in={true} timeout={500}>
                <StyledCard>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <CloudUploadIcon sx={{ color: '#1976d2', mr: 1, fontSize: 28 }} />
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        Utility Bill
                      </Typography>
                    </Box>

                    {utilityBill ? (
                      <Paper elevation={0} sx={{
                        mt: 2,
                        overflow: 'hidden',
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <img
                          alt="utility bill"
                          src={utilityBill}
                          style={{ width: '100%', display: 'block' }}
                        />
                      </Paper>
                    ) : (
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                          mt: 2,
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                          color: '#fff',
                          borderRadius: 2,
                          py: 2,
                          px: 3,
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                          },
                          width: '100%'
                        }}
                      >
                        Upload Utility Bill
                        <VisuallyHiddenInput onChange={uploadUtilityBill} type="file" />
                      </Button>
                    )}
                  </CardContent>
                </StyledCard>
              </Grow>
            </Box>
          </CustomTabPanel>
        </Box>

        {/* Notes section remains always visible at the bottom */}
        <NotesDrawer open={notesDrawerOpen} setOpen={setNotesDrawerOpen} notes={notes} setNotes={setNotes} user={user} />
      </Box>

      <PrequalAssistant
        open={openPrequal}
        onClose={() => setOpenPrequal(false)}
        prospectData={{
          ...owners[ownerTab],
          address: selectedProspect?.address,
          zip: selectedProspect?.zip,
          email: selectedProspect?.owners?.[ownerTab]?.email // Attempt to get email if available
        }}
      />
    </Dialog>
  );
}
